import { Combobox } from '@base-ui/react/combobox';
import { useCallback, useId, useMemo, useRef } from 'react';
import { DropdownTrigger } from './components/DropdownTrigger/DropdownTrigger';
import { DropdownPopup } from './components/DropdownPopup/DropdownPopup';
import { DropdownContext } from './context/DropdownContext';
import { useDropdownTree } from './hooks/useDropdownTree';
import { Description, ErrorMessage, styles } from './styles';
import type { DropdownV3Props, DropdownValue } from './types';
import type { Virtualizer } from '@tanstack/react-virtual';

export const DropdownV3 = ({
  items,
  searchable = true,
  hideSearchParents = false,
  multiple = false,
  expandable = false,
  defaultExpanded = false,
  loading = false,
  disabled = false,
  selectedValues,
  initialSelectedValues,
  initialSelectionMode,
  description,
  label = 'Dropdown with multi-select and search',
  triggerPlaceholder = 'Select items…',
  searchPlaceholder = 'e.g. Apple',
  searchNoResultsText = 'No items found.',
  searchClearLabel = 'Clear search',
  selectAllLabel = 'Select all',
  allSelectedLabel = 'All Selected',
  formatSelectedValues,
  customValueInput,
  selection,
  onSelectionChange,
  selectedValuesDisplay = 'first',
  countSelectedGroups = false,
  filterItems,
  matchesItem,
  renderItemContent,
  headerContent,
  required = false,
  showRequiredIndicator = true,
  error = false,
  errorMessage,
  fullWidth = false,
  onChange,
  'data-testid': dataTestId,
}: DropdownV3Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);
  const preserveSelectionSearchRef = useRef(false);
  const customInputRef = useRef<HTMLInputElement | null>(null);
  const focusCustomInputRef = useRef(false);
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const descriptionId = `${fieldId}-description`;
  const errorId = `${fieldId}-error`;
  const describedBy =
    [description && descriptionId, error && errorMessage && errorId].filter(Boolean).join(' ') ||
    undefined;
  const singleSelectedValues = useMemo(
    () =>
      selection ? [selection.kind === 'custom' ? selection.optionValue : selection.value] : [],
    [selection],
  );

  const dropdownState = useDropdownTree({
    items,
    multiple,
    searchable,
    hideSearchParents,
    expandable,
    defaultExpanded,
    selectedValues: customValueInput ? singleSelectedValues : selectedValues,
    initialSelectedValues,
    initialSelectionMode,
    filterItems,
    matchesItem,
    onSelectionChange: onChangeCallback,
  });

  function onChangeCallback(
    nextSelectedValues: DropdownValue[],
    event?: Combobox.Root.ChangeEventDetails,
  ): void {
    if (!onSelectionChange) {
      onChange?.(multiple ? nextSelectedValues : (nextSelectedValues[0] ?? null), event);

      return;
    }
    const value = nextSelectedValues[0];
    const item = dropdownState.allItems.find((item) => item.value === value);

    if (!item) {
      onSelectionChange(null, event);
    } else if (item.customValue && item.type === 'value') {
      onSelectionChange(
        {
          kind: 'custom',
          optionValue: item.value,
          text:
            selection?.kind === 'custom' && selection.optionValue === item.value
              ? selection.text
              : '',
        },
        event,
      );
    } else {
      onSelectionChange({ kind: 'preset', value: item.value }, event);
    }
  }
  const rowIndexByValue = useMemo(
    () => new Map(dropdownState.visibleItems.map((item, index) => [item.value, index])),
    [dropdownState.visibleItems],
  );

  const handleItemHighlighted = useCallback(
    (highlightedValue: DropdownValue | undefined, details: Combobox.Root.HighlightEventDetails) => {
      const virtualizer = virtualizerRef.current;
      const rowIndex = highlightedValue ? rowIndexByValue.get(highlightedValue) : undefined;

      if (!virtualizer || rowIndex === undefined) {
        return;
      }

      const isStart = details.index === 0;
      const isEnd = details.index === dropdownState.visibleValues.length - 1;
      const shouldScroll =
        details.reason === 'none' || (details.reason === 'keyboard' && (isStart || isEnd));

      if (shouldScroll) {
        queueMicrotask(() => {
          virtualizer.scrollToIndex(rowIndex, { align: isEnd ? 'start' : 'end' });
        });
      }
    },
    [dropdownState.visibleValues.length, rowIndexByValue],
  );

  const handleInputValueChange = useCallback(
    (nextInputValue: string, details: Combobox.Root.ChangeEventDetails) => {
      if (details.reason === 'input-clear' && preserveSelectionSearchRef.current) {
        return;
      }
      preserveSelectionSearchRef.current = false;
      dropdownState.setSearchTerm(nextInputValue);
    },
    [dropdownState],
  );

  const handleOpenChange = useCallback(
    (open: boolean, details: Combobox.Root.ChangeEventDetails) => {
      focusCustomInputRef.current =
        !open &&
        (details.reason === 'item-press' ||
          details.reason === 'escape-key' ||
          details.reason === 'trigger-press');
      if (open) {
        return;
      }
      preserveSelectionSearchRef.current = false;
      dropdownState.setSearchTerm('');
    },
    [dropdownState],
  );

  const handleValueChange = useCallback(
    (
      nextSelectedValues: DropdownValue[] | DropdownValue | null,
      details: Combobox.Root.ChangeEventDetails,
    ) => {
      preserveSelectionSearchRef.current = details.reason === 'item-press';
      dropdownState.handleSelectionChange(nextSelectedValues, details);
    },
    [dropdownState],
  );

  return (
    <DropdownContext
      value={{
        ...dropdownState,
        loading,
        multiple,
        searchable,
        expandable,
        defaultExpanded,
        label,
        triggerPlaceholder,
        searchPlaceholder,
        searchNoResultsText,
        selectAllLabel,
        allSelectedLabel,
        searchClearLabel,
        selectedValuesDisplay,
        countSelectedGroups,
        renderItemContent,
        headerContent,
        error,
        required,
        dataTestId,
      }}
    >
      <Combobox.Root
        virtualized
        // base-ui highlights the selection on open by its index in items, so items match the rows.
        items={dropdownState.visibleValues}
        filteredItems={dropdownState.visibleValues}
        multiple={multiple}
        inputValue={dropdownState.searchTerm}
        onInputValueChange={handleInputValueChange}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={(open) => {
          if (!open && focusCustomInputRef.current && selection?.kind === 'custom') {
            customInputRef.current?.focus();
          }
        }}
        onItemHighlighted={handleItemHighlighted}
        value={dropdownState.selectedValues}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <div css={styles.Container({ fullWidth })} ref={containerRef} data-testid={dataTestId}>
          <Combobox.Label css={styles.FieldLabelCss}>
            <span id={labelId}>
              {label}
              {required && showRequiredIndicator && <span aria-hidden="true"> *</span>}
            </span>
          </Combobox.Label>
          <DropdownTrigger
            formatSelectedValues={formatSelectedValues}
            editableSingleValue={
              customValueInput && selection?.kind === 'custom'
                ? {
                    value: selection.text,
                    placeholder: customValueInput.placeholder,
                    ariaLabel: label,
                    onChange: (text) => {
                      onSelectionChange({ ...selection, text });
                    },
                  }
                : undefined
            }
            labelId={labelId}
            customInputRef={customInputRef}
            describedBy={describedBy}
            disabled={disabled}
          />
          {description && (
            <div id={descriptionId}>
              <Description>{description}</Description>
            </div>
          )}
          {error && errorMessage && (
            <div id={errorId}>
              <ErrorMessage>{errorMessage}</ErrorMessage>
            </div>
          )}
        </div>

        <DropdownPopup containerRef={containerRef} virtualizerRef={virtualizerRef} />
      </Combobox.Root>
    </DropdownContext>
  );
};
